import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d70id7r2m2156g4s6t00';

const BookAppointmentPage = () => {
  const navigate = useNavigate();
  const [formType, setFormType] = useState<'appointment' | 'estimate'>('appointment');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const data = new URLSearchParams(new FormData(e.currentTarget) as unknown as Record<string, string>);
    data.append('request_type', formType);
    setStatus('submitting');
    try {
      const res = await fetch(FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: data.toString() });
      setStatus(res.ok ? 'success' : 'error');
    } catch { setStatus('error'); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <section className="bg-[#0C2340] py-16 nav-offset">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#C41E3A] text-sm font-bold uppercase tracking-widest mb-3">Scheduling</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Book Your Appointment</h1>
          <p className="text-xl text-white/75 max-w-xl mx-auto">Fast, easy scheduling. Most appointments available within 24–48 hours.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-10 items-start">
            <div className="lg:col-span-2">
              <div className="flex rounded-xl border border-[#E2E8F0] p-1 mb-8">
                {(['appointment', 'estimate'] as const).map((t) => (
                  <button
                    key={t}
                    onClick={() => setFormType(t)}
                    className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${formType === t ? 'bg-[#0C2340] text-white' : 'text-[#6B7280] hover:text-[#111827]'}`}
                  >
                    {t === 'appointment' ? 'Book Appointment' : 'Get Free Estimate'}
                  </button>
                ))}
              </div>

              {status === 'success' ? (
                <div className="text-center py-16 bg-green-50 rounded-2xl border border-green-200">
                  <i className="ri-check-circle-fill text-5xl text-green-500 mb-4" />
                  <h3 className="text-2xl font-bold text-[#111827] mb-2">Request Received!</h3>
                  <p className="text-[#6B7280] mb-6">Our team will contact you within 2 hours to confirm your appointment during business hours.</p>
                  <button onClick={() => setStatus('idle')} className="bg-[#C41E3A] text-white px-6 py-3 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors whitespace-nowrap cursor-pointer">
                    Submit Another Request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-readdy-form="true" id="booking-form" className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">First Name *</label>
                      <input name="first_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="Jane" />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-[#111827] mb-2">Last Name *</label>
                      <input name="last_name" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="Smith" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Email Address *</label>
                    <input name="email" type="email" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="jane@example.com" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Phone Number *</label>
                    <input name="phone" type="tel" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="(555) 123-4567" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Service Address *</label>
                    <input name="address" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" placeholder="123 Main St, City, State, ZIP" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Service Needed *</label>
                    <select name="service" required className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm">
                      <option value="">Select service</option>
                      <option>Heating / Furnace</option>
                      <option>Air Conditioning</option>
                      <option>Heat Pump</option>
                      <option>Plumbing</option>
                      <option>Electrical</option>
                      <option>Water Heater</option>
                      <option>Insulation</option>
                      <option>Indoor Air Quality</option>
                      <option>Maintenance Plan</option>
                      <option>Energy Assessment</option>
                      <option>Emergency Service</option>
                    </select>
                  </div>
                  {formType === 'appointment' && (
                    <>
                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">Preferred Date</label>
                        <input name="preferred_date" type="date" className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-[#111827] mb-2">Preferred Time</label>
                        <select name="preferred_time" className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm">
                          <option>Flexible</option>
                          <option>Morning (8am–12pm)</option>
                          <option>Afternoon (12pm–5pm)</option>
                          <option>Evening (5pm–8pm)</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-[#111827] mb-2">Describe Your Issue or Project</label>
                    <textarea name="description" rows={4} maxLength={500} onChange={(e) => setCharCount(e.target.value.length)} className="w-full px-4 py-3 rounded-lg border border-[#E2E8F0] focus:ring-2 focus:ring-[#0C2340] outline-none text-sm resize-none" placeholder="Tell us what's going on or what you need..." />
                    <p className="text-xs text-[#9CA3AF] mt-1">{charCount}/500</p>
                  </div>
                  {status === 'error' && <p className="text-sm text-red-600">Something went wrong. Please try again or call us directly.</p>}
                  <button type="submit" disabled={status === 'submitting'} className="w-full bg-[#C41E3A] text-white py-4 rounded-lg text-base font-semibold hover:bg-[#A01830] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer">
                    {status === 'submitting' ? 'Submitting...' : formType === 'appointment' ? 'Confirm Booking Request' : 'Request Free Estimate'}
                  </button>
                </form>
              )}
            </div>

            <div className="space-y-5">
              <div className="bg-[#F8F9FB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-base font-bold text-[#111827] mb-4">Need Emergency Service?</h3>
                <p className="text-sm text-[#6B7280] mb-4">Skip the form and call us directly. We&apos;re available 24/7.</p>
                <a href="tel:+18506698335" className="flex items-center justify-center space-x-2 bg-[#C41E3A] text-white py-3 rounded-lg text-sm font-semibold hover:bg-[#A01830] transition-colors whitespace-nowrap">
                  <i className="ri-phone-fill" />
                  <span>(850) 669-8335</span>
                </a>
              </div>
              <div className="bg-[#F8F9FB] rounded-xl p-6 border border-[#E2E8F0]">
                <h3 className="text-sm font-bold text-[#111827] mb-3">What to Expect</h3>
                <ul className="space-y-2">
                  {['Confirmation within 2 hours', 'Same-day & next-day available', 'Upfront pricing before work begins', 'NATE-certified technician', '100% satisfaction guarantee'].map((s) => (
                    <li key={s} className="flex items-center space-x-2 text-xs text-[#374151]">
                      <i className="ri-check-line text-[#C41E3A]" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-[#0C2340] rounded-xl p-6">
                <h3 className="text-sm font-bold text-white mb-2">Need Financing?</h3>
                <p className="text-white/60 text-xs mb-3">Apply for 0% APR financing before or after your appointment.</p>
                <button onClick={() => navigate('/financing')} className="w-full border border-white/30 text-white py-2.5 rounded-lg text-xs font-semibold hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer">
                  View Financing Options
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default BookAppointmentPage;
